'use client';

import { EvEbitValues } from './types';
import { ToolsTranslations } from '@/lib/translations/types';

interface CalculatorInputsProps {
  values: EvEbitValues;
  onValuesChange: (field: keyof EvEbitValues, value: number | string) => void;
  translations: ToolsTranslations['evEbit'];
}

export default function CalculatorInputs({ values, onValuesChange, translations }: CalculatorInputsProps) {
  const handleSliderChange = (field: keyof EvEbitValues, value: number) => {
    onValuesChange(field, value);
  };

  const handleInputChange = (field: keyof EvEbitValues, value: string) => {
    if (field === 'currency') {
      onValuesChange(field, value);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onValuesChange(field, numValue);
    }
  };

  return (
    <div className="space-y-6">
      {/* Enterprise Value */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="enterprise-value-input" className="text-gray-700 font-medium">{translations.enterpriseValue}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="enterprise-value-input"
              type="text"
              value={values.enterpriseValue.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('enterpriseValue', e.target.value.replace(/\s/g, ''))}
              className="w-32 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.enterpriseValue}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.enterpriseValue / 10000000000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="enterprise-value-slider"
            type="range"
            min="1000000"
            max="10000000000"
            step="1000000"
            value={values.enterpriseValue}
            onChange={(e) => handleSliderChange('enterpriseValue', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.enterpriseValue} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>1M {values.currency}</span>
          <span>10B {values.currency}</span>
        </div>
      </div>

      {/* EBIT */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg 
              className="w-4 h-4 text-blue-500 animate-pulse" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <label htmlFor="ebit-input" className="text-gray-700 font-medium">{translations.ebit}</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="ebit-input"
              type="text"
              value={values.ebit.toLocaleString('sv-SE')}
              onChange={(e) => handleInputChange('ebit', e.target.value.replace(/\s/g, ''))}
              className="w-32 text-right border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-0 px-1 py-1 text-gray-800 font-semibold bg-transparent"
              aria-label={translations.ebit}
            />
            <span className="text-gray-800 font-semibold">{values.currency}</span>
          </div>
        </div>
        <div className="relative">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min((values.ebit / 1000000000) * 100, 100)}%` }}
            ></div>
          </div>
          <input
            id="ebit-slider"
            type="range"
            min="100000"
            max="1000000000"
            step="100000"
            value={values.ebit}
            onChange={(e) => handleSliderChange('ebit', parseInt(e.target.value))}
            className="absolute inset-0 w-full h-2 cursor-pointer slider"
            aria-label={`${translations.ebit} slider`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>100K {values.currency}</span>
          <span>1B {values.currency}</span>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
